import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon, Wallet } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const navLinks = [
  { name: 'About', href: '#about' },
  { name: 'Ecosystem', href: '#ecosystem' },
  { name: 'Features', href: '#features' },
  { name: 'Roadmap', href: '#roadmap' },
  { name: 'Team', href: '#team' },
  { name: 'FAQ', href: '#faq' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme, isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? isDark 
            ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' 
            : 'bg-white/80 backdrop-blur-xl border-b border-black/10'
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <motion.a
            href="#"
            className="flex items-center gap-3 flex-shrink-0"
            whileHover={{ scale: 1.02 }}
            data-testid="navbar-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_polk-blockchain-next/artifacts/d0rayuzo_Polk%20Chain%20logo%20in%20glowing%20gradient%20design.png" 
              alt="Polk Chain Logo"
              className="h-10 sm:h-16 lg:h-20 w-auto"
            />
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <motion.a
                key={link.name}
                href={link.href}
                className={`text-xs xl:text-sm font-medium transition-colors hover:text-blue-500 whitespace-nowrap ${
                  isDark ? 'text-zinc-400' : 'text-slate-600'
                }`}
                whileHover={{ y: -2 }}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </motion.a>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Theme Toggle */}
            <motion.button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${
                isDark 
                  ? 'bg-white/10 hover:bg-white/20' 
                  : 'bg-black/5 hover:bg-black/10'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              data-testid="theme-toggle"
            >
              {isDark ? (
                <Sun className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-400" />
              ) : (
                <Moon className="w-4 sm:w-5 h-4 sm:h-5 text-slate-600" />
              )}
            </motion.button>

            {/* Connect Wallet Button */}
            <motion.button
              className="hidden sm:flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-3 sm:px-6 py-2 sm:py-2.5 font-semibold text-xs sm:text-sm transition-all shadow-[0_0_20px_rgba(0,85,255,0.3)] hover:shadow-[0_0_30px_rgba(0,85,255,0.5)]"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="connect-wallet-btn"
            >
              <Wallet className="w-3 sm:w-4 h-3 sm:h-4" />
              Connect Wallet
            </motion.button>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg ${
                isDark ? 'bg-white/10' : 'bg-black/5'
              }`}
              whileTap={{ scale: 0.9 }}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? (
                <X className={`w-5 sm:w-6 h-5 sm:h-6 ${isDark ? 'text-white' : 'text-gray-900'}`} />
              ) : (
                <Menu className={`w-5 sm:w-6 h-5 sm:h-6 ${isDark ? 'text-white' : 'text-gray-900'}`} />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`lg:hidden backdrop-blur-xl border-t overflow-y-auto max-h-[calc(100vh-80px)] ${
              isDark 
                ? 'bg-black/95 border-white/10' 
                : 'bg-white/95 border-black/10'
            }`}
            data-testid="mobile-menu"
          >
            <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-3 sm:space-y-4">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block text-base sm:text-lg font-medium transition-colors hover:text-blue-500 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {link.name}
                </motion.a>
              ))}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-full px-4 sm:px-6 py-2.5 sm:py-3 font-semibold text-sm sm:text-base mt-4"
                data-testid="mobile-connect-wallet-btn"
              >
                <Wallet className="w-4 h-4" />
                Connect Wallet
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
